import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { FileService } from '@core/services/file.service';
import { EmployeeModel } from 'services/models/employee.model';
import { ActionPlanStatus } from 'services/models/corporate/action-plan/enum/ActionPlanStatus.enum';
import { FileContentModel } from 'services/models/fileContent';

interface DropzoneFileChange {
  files?: Array<{ file: File }>;
}

@Component({
  selector: 'app-action-plan-form-list',
  templateUrl: './action-plan-form-list.component.html',
  styleUrls: ['./action-plan-form-list.component.scss'],
})
export class ActionPlanFormListComponent {
  @Input() actionList: FormArray;
  @Input() actionTypeOptions: any[] = [];
  @Input() actionStatusOptions: any[] = [];
  @Input() employees$: Observable<EmployeeModel[]>;
  @Input() employeesLoading = false;
  @Input() employeeSearchInput$: Subject<string>;
  @Input() readOnly = false;

  @Output() addActionItem = new EventEmitter<void>();
  @Output() removeActionItem = new EventEmitter<number>();
  @Output() getActionFile = new EventEmitter<string>();

  constructor(private fileService: FileService) {}

  get controls(): AbstractControl[] {
    return this.actionList?.controls ?? [];
  }

  onAddClicked(): void {
    if (this.readOnly) {
      return;
    }
    this.addActionItem.emit();
  }

  onRemoveClicked(index: number, event: MouseEvent): void {
    event.stopPropagation();

    if (this.readOnly) {
      return;
    }

    this.removeActionItem.emit(index);
  }

  trackByIndex(index: number): number {
    return index;
  }

  hasResolutionData(index: number): boolean {
    const action = this.actionList?.at(index) as FormGroup;
    const status = action?.get('status')?.value;
    if (!status) return false;
    return (
      status === ActionPlanStatus.IN_PROGRESS.value ||
      status === ActionPlanStatus.COMPLETED.value
    );
  }

  getResolutionFileContent(index: number): FileContentModel | null {
    const action = this.actionList?.at(index) as FormGroup;
    const newFile = action?.get('resolutionFileContent')?.value as FileContentModel | null;
    if (newFile?.fileName || newFile?.encodedBase64) {
      return newFile;
    }
    const fileName = action?.get('resolutionFileName')?.value;
    const fileKey = action?.get('resolutionFileKey')?.value;
    if (!fileName && !fileKey) return null;
    return {
      fileName: fileName ?? '',
      extension: '',
      encodedBase64: '',
      fileKey: fileKey ?? undefined,
    };
  }

  async onResolutionFileChange(fileData: DropzoneFileChange, index: number): Promise<void> {
    const fileContent = await this.processSelectedFile(fileData);
    const action = this.actionList?.at(index) as FormGroup;
    action?.patchValue({ resolutionFileContent: fileContent });
  }

  private async processSelectedFile(fileData: DropzoneFileChange): Promise<FileContentModel> {
    let fileContent: FileContentModel = {
      fileName: '',
      extension: '',
      encodedBase64: '',
    };
    if (fileData?.files?.length) {
      const file = fileData.files[0]?.file;
      const stringBase64 = (await this.fileService.fileToBase64(file)) as string;
      const [, encodedBase64] = stringBase64.split('base64,');
      fileContent = {
        fileName: file.name,
        extension: file.name.split('.').pop() || '',
        encodedBase64,
      };
    }
    return fileContent;
  }

  onGetActionFile(index: number): void {
    const action = this.actionList?.at(index) as FormGroup;
    const id = action?.get('id')?.value;
    if (id) this.getActionFile.emit(id);
  }
}

